import { Box } from '@mui/material';
import { MouseEvent } from 'react';
import useWidth from '../../../../../../components/Hooks/useWidth';
import { useMediaApplicationContext } from '../../../../../../components/MediaApplicationContext';
import MediaDotsIndicator from '../../../../../../components/MediaDotsIndicator';
import TeceoSlicker from '../../../../../../components/Slicker/TeceoSlicker';
import TeceoSlickerLib from '../../../../../../components/Slicker/TeceoSlickerLib';
import { MediaType } from '../../../../../../types/general.types';
import { ProductMedia } from '../../../../ProductTypes';
import { getFocusZoomPercentToScroll } from '../../utils/getFocusZoomPercentToScroll';
import GalleryMedia from '../GalleryMedia/GalleryMedia';
import ProductGalleryNavButtons from '../ProductGalleryNavButtons/ProductGalleryNavButtons';

interface GalleryMainMediaProps {
  medias: ProductMedia[];
  selectedIndex: number;
  className: string;
  dotsClassName: string;
  onMobileContainerCover?: boolean;
  onSelectIndex: (index: number) => void;
  onZoom: (media: ProductMedia, focusPercentToScroll?: number) => void;
}

const GalleryMainMedia = ({
  medias,
  selectedIndex,
  className,
  dotsClassName,
  onMobileContainerCover,
  onSelectIndex,
  onZoom,
}: GalleryMainMediaProps) => {
  const { mobileWidth, mediumDownWidth, largeWidthUp } = useWidth();
  const mediaContext = useMediaApplicationContext();

  const showMediaDots = !mediumDownWidth;

  const navigate = (direction: 1 | -1) => {
    onSelectIndex(TeceoSlickerLib.getNextIndex(selectedIndex, direction, medias.length));
  };

  const handleItemClick = (item: ProductMedia, event: MouseEvent<HTMLDivElement>) => {
    if (item.type !== MediaType.IMAGE) {
      return;
    }

    onZoom(item, getFocusZoomPercentToScroll(event));
  };

  const handleMouseEnter = () => {
    const media = medias[selectedIndex];

    if (!largeWidthUp || !media || media.type !== MediaType.IMAGE) {
      return;
    }

    const image = new Image();
    image.src = mediaContext.getMediaFromSize(media.url, 'lg', 'webp');
  };

  return (
    <Box className={className} onMouseEnter={handleMouseEnter}>
      <TeceoSlicker<ProductMedia>
        keyAcessor="id"
        selectedIndex={selectedIndex}
        onChangeSelectedIndex={onSelectIndex}
        items={medias}
        onItemClick={handleItemClick}
        selectCursor={medias[selectedIndex]?.type === 'IMAGE' ? 'crosshair' : undefined}
        renderItem={(media) => (
          <GalleryMedia media={media} onMobileContainerCover={onMobileContainerCover} />
        )}
      />

      {showMediaDots && (
        <MediaDotsIndicator
          dots={medias.length}
          selectedIndex={selectedIndex}
          onChangeIndex={onSelectIndex}
          classes={{ root: dotsClassName }}
        />
      )}

      {mobileWidth && medias.length > 1 && (
        <ProductGalleryNavButtons onPrevious={() => navigate(-1)} onNext={() => navigate(1)} />
      )}
    </Box>
  );
};

export default GalleryMainMedia;
